import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authAPI, setAuthToken, clearAuthToken } from './api';
import api from './api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [trainer, setTrainer] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch {
      // Ignore errors during logout
    }
    clearAuthToken();
    setTrainer(null);
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Session polling: validates token every 30s, auto-logouts on 401
  useEffect(() => {
    if (!trainer) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }

    const checkSession = async () => {
      try {
        await api.get('/auth/check');
      } catch (err) {
        if (err.response?.status === 401) {
          clearAuthToken();
          setTrainer(null);
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          window.location.href = '/login';
        }
      }
    };

    pollRef.current = setInterval(checkSession, 30000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [trainer]);

  // On mount: restore session from persisted token or cookie fallback
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      // If we have a persisted token, set it first so /auth/me uses Bearer header
      const persistedToken = localStorage.getItem('ft_auth_token');
      if (persistedToken) {
        setAuthToken(persistedToken);
      }
      try {
        const response = await authAPI.getMe();
        if (response.data.access_token) {
          setAuthToken(response.data.access_token);
          setTrainer(response.data.trainer);
        } else {
          setTrainer(response.data);
        }
      } catch {
        clearAuthToken();
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, trainer: trainerData } = response.data;
    // Store token in memory for Bearer header on subsequent requests
    if (access_token) {
      setAuthToken(access_token);
    }
    setTrainer(trainerData);
    return trainerData;
  };

  const loginWithOTP = async (phone, otp, name) => {
    const response = await authAPI.verifyOTP({ phone, otp, name: name || undefined });
    const data = response.data;
    // If new user and name not provided yet
    if (data.needs_name) {
      return { needs_name: true };
    }
    const { access_token, trainer: trainerData } = data;
    if (access_token) {
      setAuthToken(access_token);
    }
    setTrainer(trainerData);
    return { ...trainerData, is_new_user: data.is_new_user };
  };

  const loginWithGoogle = async (sessionId) => {
    const response = await authAPI.googleSession(sessionId);
    const { access_token, trainer: trainerData } = response.data;
    if (access_token) {
      setAuthToken(access_token);
    }
    setTrainer(trainerData);
    return { ...trainerData, is_new_user: response.data.is_new_user };
  };

  const requestAccess = async (name, email, password, captchaToken, inviteCode) => {
    const response = await authAPI.requestAccess({ name, email, password, captcha_token: captchaToken, invite_code: inviteCode || "" });
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await authAPI.register({ name, email, password });
    const { access_token, trainer: trainerData } = response.data;
    if (access_token) {
      setAuthToken(access_token);
    }
    setTrainer(trainerData);
    return trainerData;
  };

  const refreshTrainer = async () => {
    try {
      const response = await api.get('/profile');
      const updatedTrainer = { ...trainer, ...response.data };
      setTrainer(updatedTrainer);
    } catch (error) {
      console.error('Failed to refresh trainer data');
    }
  };

  const isAuthenticated = !!trainer;
  const isAdmin = trainer?.is_admin || false;

  return (
    <AuthContext.Provider value={{ trainer, setTrainer, loading, login, loginWithOTP, loginWithGoogle, register, requestAccess, logout, refreshTrainer, isAuthenticated, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
